import { useNavigate } from "react-router-dom";
import { X, Trash2, AlertTriangle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { uploadUrl } from "@/lib/api";
import { fmtCAD } from "@/lib/currency";

export default function OrderDrawer() {
  const { items, updateQty, removeItem, subtotal, totalUnits, moqViolations, minOrderUnits, belowMinOrder, unitsToMin, drawerOpen, closeDrawer } = useCart();
  const { isApproved, user } = useAuth();
  const nav = useNavigate();

  if (!drawerOpen) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="order-drawer">
      <button
        aria-label="Close drawer"
        onClick={closeDrawer}
        className="absolute inset-0 bg-foreground/30 backdrop-blur-[1px]"
      />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-white border-l border-border drawer-enter flex flex-col">
        <header className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Order pad</p>
            <h2 className="text-base font-semibold tabular-nums">{totalUnits} units · {fmtCAD(subtotal)}</h2>
          </div>
          <button onClick={closeDrawer} className="p-2 -mr-2 hover:bg-muted rounded-md" data-testid="drawer-close" aria-label="Close">
            <X size={18} strokeWidth={1.5} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {items.length === 0 ? (
            <div className="px-5 py-16 text-center" data-testid="drawer-empty">
              <p className="text-sm text-muted-foreground">No items yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Add styles from the products page to build your order.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((it) => {
                const line = (it.wholesale_price || 0) * it.quantity;
                return (
                  <li key={`${it.product_id}::${it.size}`} className="px-5 py-3 flex gap-3 items-center" data-testid="drawer-item">
                    <div className="w-12 h-14 rounded bg-secondary overflow-hidden shrink-0">
                      {it.image && <img src={uploadUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{it.name}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {it.size ? `Size ${it.size} · ` : ""}{fmtCAD(it.wholesale_price)} ea
                      </p>
                    </div>
                    <input
                      data-testid={`drawer-qty-${it.product_id}`}
                      type="number"
                      min={0}
                      value={it.quantity}
                      onChange={(e) => updateQty(it.product_id, it.size, e.target.value)}
                      className="w-14 px-2 py-1.5 text-sm text-center border rounded tabular-nums focus:border-primary border-border"
                    />
                    <p className="text-sm w-16 text-right tabular-nums">{fmtCAD(line)}</p>
                    <button
                      onClick={() => removeItem(it.product_id, it.size)}
                      className="p-1.5 text-muted-foreground hover:text-destructive"
                      aria-label="Remove"
                      data-testid={`drawer-remove-${it.product_id}`}
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="border-t border-border px-5 py-4 space-y-3 bg-white">
          {belowMinOrder && (
            <div className="text-xs text-destructive flex items-start gap-2" data-testid="drawer-min-order-warning">
              <AlertTriangle size={13} strokeWidth={1.5} className="mt-0.5 shrink-0" />
              <span>Add {unitsToMin} more unit{unitsToMin === 1 ? "" : "s"} — minimum order is {minOrderUnits} units.</span>
            </div>
          )}
          <div className="flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Units · Subtotal</span>
            <span className="text-lg font-semibold tabular-nums" data-testid="drawer-subtotal">{totalUnits} · {fmtCAD(subtotal)}</span>
          </div>
          {!isApproved ? (
            <p className="text-xs text-muted-foreground">
              {user ? "Your account is pending approval — you'll be able to submit orders once approved." : "Sign in with an approved account to submit orders."}
            </p>
          ) : (
            <button
              onClick={() => { closeDrawer(); nav("/checkout"); }}
              disabled={items.length === 0 || belowMinOrder}
              data-testid="drawer-checkout"
              className="w-full py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-95 transition-opacity disabled:opacity-40"
            >
              Review &amp; submit wholesale order
            </button>
          )}
          <button
            onClick={() => { closeDrawer(); nav("/cart"); }}
            className="w-full py-2 text-xs uppercase tracking-[0.12em] text-muted-foreground hover:text-foreground"
            data-testid="drawer-view-cart"
          >
            Open full order pad →
          </button>
        </footer>
      </aside>
    </div>
  );
}
